import { ModulePreviewViewport } from '../module-preview-viewport';
import '../../assets/entity-list.css';
import { Dialog, DialogHandle } from './dialog';
import { Button } from "primereact/button";
import { HtmlViewer } from "../htmlViewer";
import { useEffect, useRef, useState } from "react";
import { Splitter, SplitterPanel } from "primereact/splitter";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { toggleFullscreen } from '../../general/browser';

type TestModuleDialogProps = {
  visible: boolean;
  htmlContent: string | null;
  theme: string;
  instructions: string | null;
  onClose: () => void;
};

interface MessageDto {
    id: number,
    timestamp: string,
    payload: any
}

export function TestModuleDialog({
  visible,
  htmlContent,
  theme,
  instructions,
  onClose
}: TestModuleDialogProps) {
    const tableWrapperRef = useRef<HTMLDivElement | null>(null);
    const [messages, setMessages] = useState<MessageDto[]>([]);
    const [selectedMessage, setSelectedMessage] = useState<MessageDto | null>(null);
    const [topSize, setTopSize] = useState(75); // default 75% for HtmlViewer

    const dialogRef = useRef<DialogHandle>(null);
  const dialogContainerRef = useRef<HTMLDivElement | null>(null);
    const [previewControlsTarget, setPreviewControlsTarget] = useState<HTMLDivElement | null>(null);
      const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
    
    useEffect(() => {
        const handler = () => setIsFullscreen(!!document.fullscreenElement);
        document.addEventListener("fullscreenchange", handler);
        return () => document.removeEventListener("fullscreenchange", handler);
    }, []);
    
    const onShow = () => {
        // PrimeReact renders the dialog DOM AFTER visible=true
        const container = dialogRef.current?.getElement() ?? null;
        dialogContainerRef.current = container;
    };
    
    const header = (
        <div className="module-preview-header flex align-items-center justify-content-between w-full">
            <span className="text-lg font-semibold">Module interaction testing</span>
    
            <div className="module-preview-header-actions">
              <div className="module-preview-device-slot" ref={setPreviewControlsTarget} />
              <Button
                  aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
                  icon={isFullscreen ? "pi pi-window-minimize" : "pi pi-window-maximize"}
                  className="p-button-text p-button-rounded"
                  onClick={() => {
                      if (dialogContainerRef.current) {
                          toggleFullscreen(dialogContainerRef.current);
                      }
                  }}
              />
            </div>
        </div>
    );

    useEffect(() => {
        const wrapper = tableWrapperRef.current?.querySelector(".p-datatable-wrapper");
        if (wrapper) {
            wrapper.scrollTop = wrapper.scrollHeight;
        }
    }, [messages]);

    const handleIframeMessage = (data: any) => {
        setMessages((prev) => [...prev, { id: prev.length + 1, timestamp: new Date().toISOString(), payload: data } as MessageDto]);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (!messages.length) return;

        const currentIndex = selectedMessage
            ? messages.findIndex(m => m.id === selectedMessage.id)
            : -1;

        if (e.key === "ArrowDown") {
            const next = Math.min(messages.length - 1, currentIndex + 1);
            setSelectedMessage(messages[next]);
        }

        if (e.key === "ArrowUp") {
            const prev = Math.max(0, currentIndex - 1);
            setSelectedMessage(messages[prev]);
        }
    };

    return (
        <Dialog
        ref={dialogRef}
            header={header}
            visible={visible}
            onShow={onShow}
            onHide={() => {
                setMessages([]);
                setSelectedMessage(null);
                onClose();
            }}
            modal
            draggable={false}
            style={{ width: "calc(100vw - 2rem)", height: "calc(100vh - 2rem)", maxWidth: "100%", maxHeight: "100%", boxShadow: "0 0 20px rgba(0, 0, 0, 0.2)" }}
            contentStyle={{ display: "flex", flexDirection: "column", height: "100%", overflow: "auto", padding: "1rem" }}
            transitionOptions={{ classNames: "p-dialog", timeout: 300 }}
            >
                <Splitter layout="vertical" style={{ height: "100%" }} onResizeEnd={(e) => setTopSize(e.sizes[0])}>
                    <SplitterPanel size={topSize} style={{ display: 'flex', minHeight: 0 }}>
                    <ModulePreviewViewport controlsTarget={previewControlsTarget}>
                        <div className="entity-list-container">
                            <div className="entity-list-wrapper" style={{ isolation: 'isolate' }}>
                                <HtmlViewer
                                    htmlContent={htmlContent ?? ''}
                                    theme={theme}
                                    instructions={instructions ?? ''}
                                    onMessage={(kind, data) => { handleIframeMessage(data); }}
                                    selectedMode={'preview'}
                                    readOnly={true}
                                    hideControls={true}
                                    sandboxStyles={true}
                                />
                            </div>
                        </div>
                    </ModulePreviewViewport>
                    </SplitterPanel>

                    <SplitterPanel size={100 - topSize} minSize={10}>
                    <div style={{ display: "flex", height: "100%", minHeight: 0, width: '100%' }}>
                        <div ref={tableWrapperRef} style={{ flex: 1, minWidth: 0, minHeight: 0, display: "flex", flexDirection: "column" }} >
                            <DataTable
                                value={messages}
                                selectionMode="single"
                                selection={selectedMessage}
                                onSelectionChange={(e) => setSelectedMessage(e.value as MessageDto)}
                                scrollable
                                scrollHeight="100%"
                                size="small"
                                emptyMessage={<div className="flex align-items-center gap-2"><i className="pi pi-info-circle" /><span>The document fired no events so far.</span></div>}
                                onKeyDown={handleKeyDown}
                            >
                                <Column field="id" header="#" style={{ width: "60px" }} />
                                <Column field="timestamp" header="Timestamp" />
                                <Column header="Message" body={(row) => String(row.payload)} />
                            </DataTable>
                        </div>
                        <div style={{ flex: 1, minWidth: 0, minHeight: 0, paddingLeft: "0.75rem" }}>
                            <div>Preview:</div>
                            <textarea
                                readOnly
                                value={
                                selectedMessage
                                    ? typeof selectedMessage.payload === "object"
                                    ? JSON.stringify(selectedMessage.payload, null, 2)
                                    : String(selectedMessage.payload)
                                    : ""
                                }
                                style={{
                                width: "100%",
                                height: "100%",
                                resize: "none",
                                padding: "0.75rem",
                                fontFamily: "monospace",
                                fontSize: "0.85rem",
                                whiteSpace: "pre-wrap",
                                background: "var(--surface-ground)",
                                border: "1px solid var(--surface-border)",
                                borderRadius: "4px"
                                }}
                            />
                        </div>

                    </div>
                    </SplitterPanel>
                </Splitter>

                <div className="flex justify-content-end mt-4">
                    <Button
                    label="Close"
                    icon="pi pi-times"
                    onClick={() => {
                        setMessages([]);
                        setSelectedMessage(null);
                        onClose();
                    }}
                    className="p-button-secondary"
                    />
                </div>
        </Dialog>
    );
}
